import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  Select, MenuItem, InputAdornment, IconButton,
} from "@mui/material";
import CloseIcon              from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { postData }           from "../../BaseUrl";
import Toast                  from "../../DynamicComponent/Toast";

/* ─── Options ──────────────────────────────────────────── */
const GRADE_OPTIONS       = ["Nilai 8A", "Nilai 8B", "Nilai 8C", "Nilai05"];
const CLASS_OPTIONS       = ["Morning", "Afternoon", "Evening"];
const GRADE_LETTER_OPTIONS = ["A+", "A", "B+", "B", "C+", "C"];

/* ─── Shared field style ────────────────────────────────── */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#FAFAFA",
    fontSize: "13.5px",
    "& fieldset":            { borderColor: "#E5E7EB" },
    "&:hover fieldset":      { borderColor: "#9CA3AF" },
    "&.Mui-focused fieldset":{ borderColor: "#336599" },
  },
  "& input::placeholder":    { color: "#9CA3AF", opacity: 1 },
  "& textarea::placeholder": { color: "#9CA3AF", opacity: 1 },
};

const selectSx = {
  borderRadius: "8px",
  backgroundColor: "#FAFAFA",
  fontSize: "13.5px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#336599" },
};

/* ─── Component ─────────────────────────────────────────── */
const BiliteracyEnrollmentForm = ({ handleClose, applicationId, isInline = false }) => {
  const [formValues, setFormValues] = useState({
    firstName:           "",
    registrationNo:      "REG001",
    gradeSection:        "Nilai05",
    class:               "",
    overallGrade:        "",
    date:                new Date().toISOString().split("T")[0],
    googleFormText:      "",
    parentName:          "",
    parentEmail:         "",
    parentPhone:         "98765432667",
    declarationAccepted: false,
  });
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState({ open: false, severity: "success", message: "" });

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    ["firstName","registrationNo","gradeSection","class","overallGrade","date","parentName","parentEmail","parentPhone"]
      .forEach((f) => { if (!formValues[f]) newErrors[f] = "Required"; });
    if (!formValues.declarationAccepted)
      newErrors.declarationAccepted = "You must accept the declaration";
    if (formValues.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.parentEmail))
      newErrors.parentEmail = "Invalid email";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload  = { ...formValues, applicationId };
      const response = await postData("student/biliteracy/enroll", payload);
      if (response?.success || response?.data) {
        setToast({ open: true, severity: "success", message: "Enrollment submitted successfully!" });
        setTimeout(() => handleClose(), 1500);
      }
    } catch {
      setToast({ open: true, severity: "error", message: "Failed to submit enrollment. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast((p) => ({ ...p, open: false }))}
        severity={toast.severity}
        message={toast.message}
      />

      {/* ── Header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "20px", color: "#111827" }}>
            New Registration
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#9CA3AF", mt: 0.3 }}>
            Seal of Biliteracy &middot; Fill all required fields
          </Typography>
        </Box>
        {!isInline && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        )}
      </Box>

      {/* ── Section label ── */}
      <Typography sx={{
        fontSize: "11px", fontWeight: 700, color: "#F59E0B",
        letterSpacing: "0.10em", textTransform: "uppercase", mt: 2.5, mb: 2,
      }}>
        Application Form
      </Typography>

      {/* ── Form ── */}
      <Grid container spacing={2}>

        {/* Row 1: First Name | Registration No */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="firstName" value={formValues.firstName} onChange={handleChange}
            placeholder="First Name" fullWidth size="small"
            error={!!errors.firstName} helperText={errors.firstName}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="registrationNo" value={formValues.registrationNo} onChange={handleChange}
            placeholder="Registration No" fullWidth size="small"
            error={!!errors.registrationNo} helperText={errors.registrationNo}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 2: Grade & Section | Class */}
        <Grid item xs={12} sm={6}>
          <Select
            name="gradeSection" value={formValues.gradeSection} onChange={handleChange}
            displayEmpty fullWidth size="small" error={!!errors.gradeSection}
            sx={selectSx}
          >
            <MenuItem value="" disabled sx={{ color: "#9CA3AF" }}>Grade &amp; Section</MenuItem>
            {GRADE_OPTIONS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
          {errors.gradeSection && (
            <Typography sx={{ fontSize: "11px", color: "#f44336", ml: 1.5, mt: 0.3 }}>
              {errors.gradeSection}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            name="class" value={formValues.class} onChange={handleChange}
            displayEmpty fullWidth size="small" error={!!errors.class}
            renderValue={(v) => v || <span style={{ color: "#9CA3AF" }}>Select</span>}
            sx={selectSx}
          >
            <MenuItem value="" disabled sx={{ color: "#9CA3AF" }}>Select</MenuItem>
            {CLASS_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
          {errors.class && (
            <Typography sx={{ fontSize: "11px", color: "#f44336", ml: 1.5, mt: 0.3 }}>
              {errors.class}
            </Typography>
          )}
        </Grid>

        {/* Row 3: Overall Grade | Date */}
        <Grid item xs={12} sm={6}>
          <Select
            name="overallGrade" value={formValues.overallGrade} onChange={handleChange}
            displayEmpty fullWidth size="small" error={!!errors.overallGrade}
            renderValue={(v) => v || <span style={{ color: "#9CA3AF" }}>%</span>}
            sx={selectSx}
          >
            <MenuItem value="" disabled sx={{ color: "#9CA3AF" }}>Overall Grade</MenuItem>
            {GRADE_LETTER_OPTIONS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
          {errors.overallGrade && (
            <Typography sx={{ fontSize: "11px", color: "#f44336", ml: 1.5, mt: 0.3 }}>
              {errors.overallGrade}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="date" type="date" value={formValues.date} onChange={handleChange}
            fullWidth size="small"
            error={!!errors.date} helperText={errors.date}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarTodayOutlinedIcon sx={{ fontSize: "16px", color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 4: Google Form full-width */}
        <Grid item xs={12}>
          <TextField
            name="googleFormText" value={formValues.googleFormText} onChange={handleChange}
            placeholder="Tell us about your Tamil language journey"
            multiline rows={3} fullWidth size="small"
            sx={fieldSx}
          />
        </Grid>

        {/* Row 5: Parent/Guardian | Parent Email */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="parentName" value={formValues.parentName} onChange={handleChange}
            placeholder="Parent/Guardian" fullWidth size="small"
            error={!!errors.parentName} helperText={errors.parentName}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="parentEmail" type="email" value={formValues.parentEmail} onChange={handleChange}
            placeholder="Email" fullWidth size="small"
            error={!!errors.parentEmail} helperText={errors.parentEmail}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 6: Parent Phone full-width */}
        <Grid item xs={12}>
          <TextField
            name="parentPhone" value={formValues.parentPhone} onChange={handleChange}
            placeholder="Parent Phone No" fullWidth size="small"
            error={!!errors.parentPhone} helperText={errors.parentPhone}
            sx={fieldSx}
          />
        </Grid>
      </Grid>

      {/* ── Action buttons ── */}
      <Box sx={{ display: "flex", gap: 2, mt: 3.5, justifyContent: "center" }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            border: "1.5px solid #D1D5DB", color: "#374151",
            textTransform: "none", fontSize: "14px",
            px: 5, py: 1.1, borderRadius: "8px", fontWeight: 500,
            "&:hover": { backgroundColor: "#F9FAFB" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: "#2D3A5C", color: "#fff",
            textTransform: "none", fontSize: "14px",
            px: 5, py: 1.1, borderRadius: "8px", fontWeight: 600,
            "&:hover": { backgroundColor: "#1E2A45" },
            "&.Mui-disabled": { backgroundColor: "#9CA3AF", color: "#fff" },
          }}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );
};

export default BiliteracyEnrollmentForm;
